// Редактор зала: залы и столы. Позиции на плане — фаза графического
// редактора; сейчас важнее, чтобы касса и брони знали столы по именам.
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { HallPlanEditor } from './HallPlanEditor';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LocationsList, TerminalsList } from '@dastarhan/contracts';
import { z } from 'zod';

const API = '/api/v1';

const AdminHalls = z.array(z.object({
  id: z.string(), name: z.string(), isActive: z.boolean(),
  /** процент зала: действует на столы, где не задано своего */
  servicePct: z.number().int().nullable().optional(),
  tables: z.array(z.object({
    id: z.string(), name: z.string(), seats: z.number(), isActive: z.boolean(),
    /** свой процент обслуживания; null — брать зальный или общий */
    servicePct: z.number().int().nullable().optional(),
  })),
}));

export function HallScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const [serviceFor, setServiceFor] = useState<{ id: string; name: string; servicePct?: number | null } | null>(null);
  // Поле заполняем текущим значением: владелец чаще правит «было 12,
  // стало 14», чем задаёт с чистого листа
  const [svcOwn, setSvcOwn] = useState('');
  const [serviceForHall, setServiceForHall] = useState<{
    id: string; name: string; servicePct?: number | null;
    /* имя нужно, чтобы в подтверждении показать столы поимённо */
    tables: { id: string; name: string; servicePct?: number | null }[];
  } | null>(null);
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const hh = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const [locationId, setLocationId] = useState<string | null>(null);
  const loc = locationId ?? locations.data?.[0]?.id ?? null;

  const terminals = useQuery({
    queryKey: ['terminals', loc],
    enabled: !!loc,
    refetchInterval: 30_000,
    queryFn: async () => TerminalsList.parse(
      await (await fetch(`${API}/terminals?locationId=${loc}`, { headers: hh })).json(),
    ),
  });
  const halls = useQuery({
    queryKey: ['halls', loc],
    enabled: !!loc,
    queryFn: async () =>
      AdminHalls.parse(await (await fetch(`${API}/hall/admin?locationId=${loc}`, { headers: h })).json()),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['halls'] });

  const createHall = useMutation({
    mutationFn: (name: string) =>
      fetch(`${API}/hall/halls?locationId=${loc}`, { method: 'POST', headers: jh, body: JSON.stringify({ name }) }),
    onSuccess: refresh,
  });
  const patchHall = useMutation({
    mutationFn: ({ id, ...b }: {
      id: string; name?: string; isActive?: boolean; servicePct?: number | null;
    }) =>
      fetch(`${API}/hall/halls/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const addTable = useMutation({
    mutationFn: ({ hallId, ...b }: { hallId: string; name: string; seats: number }) =>
      fetch(`${API}/hall/halls/${hallId}/tables`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const patchTable = useMutation({
    mutationFn: ({ id, ...b }: {
      id: string; name?: string; seats?: number; isActive?: boolean;
      servicePct?: number | null;
    }) =>
      fetch(`${API}/hall/tables/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });

  const [hallName, setHallName] = useState('');
  const [draft, setDraft] = useState<{ hallId: string; name: string; seats: string } | null>(null);

  if (locations.isPending || (loc && halls.isPending)) return <p className="empty">Загружаем зал…</p>;
  if (!loc) return <p className="empty">Сначала нужна точка — она создаётся при регистрации.</p>;
  if (halls.isError || !halls.data) return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;

  return (
    <>
    <div className="hall-edit">
      <div className="menu-toolbar">
        <span className="row" style={{ gap: 6 }}>
          <span className="hint-inline">Витрина:</span>
          <input type="time" defaultValue={undefined} key={`o-${loc}`} id="loc-open"
            style={{ width: 110 }} title="Открываемся" />
          <span className="hint-inline">–</span>
          <input type="time" key={`c-${loc}`} id="loc-close" style={{ width: 110 }} title="Закрываемся" />
          <button className="btn small" onClick={() => void (async () => {
            if (!loc) return;
            const open = (document.getElementById('loc-open') as HTMLInputElement).value || null;
            const close = (document.getElementById('loc-close') as HTMLInputElement).value || null;
            const a = await ask({
              title: 'Расписание для витрины',
              sub: 'Часы работы уже сохранятся из полей выше. Эта строка — для гостя: сюда пишут перерыв и выходные.',
              value: {
                label: 'Как написать гостю',
                initial: '',
                allowEmpty: true,
                placeholder: 'Пн–Чт 10–23, Пт–Сб 10–01, Вс выходной',
                hint: 'пусто — показывать только часы',
              },
              confirmLabel: 'Сохранить',
            });
            if (!a) return;
            const hoursNote = a.value;
            void fetch(`${API}/menu/locations/${loc}`, {
              method: 'PATCH', headers: jh,
              body: JSON.stringify({ openTime: open, closeTime: close, hoursNote: hoursNote || null }),
            }).then((r) => note(r.ok
              ? (open && close ? `Витрина покажет режим ${open}–${close}` : 'Режим убран с витрины')
              : 'Не сохранилось — проверьте время', r.ok ? 'ok' : 'bad'));
          })()}>Часы</button>
          <button className="btn small" title="Телефон и адрес — в подвале витрины"
            onClick={() => void (async () => {
              if (!loc) return;
              /* Три поля одной формой: раньше это были три окошка подряд,
                 и на втором человек уже не помнил, что ввёл в первом. */
              const a = await ask({
                title: 'Контакты заведения',
                sub: 'Гость видит их в подвале витрины — по ним звонят и находят вас.',
                fields: [
                  { id: 'phone', label: 'Телефон', initial: '+7 ', placeholder: '+7 701 123-45-67', hint: 'пусто — не показывать' },
                  { id: 'address', label: 'Адрес', placeholder: 'Алматы, Абая 15', hint: 'пусто — не показывать' },
                  { id: 'year', label: 'Год основания', numeric: true, placeholder: '2014', hint: 'четыре цифры; пусто — не показывать' },
                ],
                confirmLabel: 'Сохранить',
              });
              if (!a) return;
              const fy = a.values.year ?? '';
              const r = await fetch(`${API}/menu/locations/${loc}`, {
                method: 'PATCH', headers: jh,
                body: JSON.stringify({
                  phone: a.values.phone || null,
                  address: a.values.address || null,
                  foundedYear: /^\d{4}$/.test(fy) ? Number(fy) : null,
                }),
              });
              note(r.ok ? 'Витрина покажет контакты в подвале' : 'Не сохранилось', r.ok ? 'ok' : 'bad');
            })()}>Контакты</button>
          <button className="btn small" title="Внутренняя заметка — гость её не видит"
            onClick={() => void (async () => {
              if (!loc) return;
              const a = await ask({
                title: 'Заметка к точке',
                sub: 'Видна только в офисе — гость её не увидит.',
                value: {
                  label: 'О чём помнить',
                  initial: '',
                  multiline: true,
                  allowEmpty: true,
                  placeholder: 'Ключи от веранды у охраны, вход со двора',
                  hint: 'пусто — убрать заметку',
                },
                confirmLabel: 'Сохранить',
              });
              if (!a) return;
              const r = await fetch(`${API}/menu/locations/${loc}`, {
                method: 'PATCH', headers: jh,
                body: JSON.stringify({ note: a.value || null }),
              });
              note(r.ok ? 'Заметка сохранена — видна только в офисе' : 'Не сохранилось', r.ok ? 'ok' : 'bad');
            })()}>Заметка</button>
          <button className="btn small" title="Летник на зиму: скрыть из списков, история цела"
            onClick={() => void (async () => {
              if (!loc) return;
              const cur = locations.data?.find((l) => l.id === loc);
              const toArchive = cur?.isActive !== false;
              const a = await ask({
                title: toArchive ? `Убрать «${cur?.name}» в архив` : `Вернуть «${cur?.name}» из архива`,
                sub: toArchive
                  ? 'Точка исчезнет из списков — так убирают летник на зиму.'
                  : 'Точка снова появится в списках и на витрине.',
                effects: [
                  ['Точка', cur?.name ?? '—'],
                  ['Отчёты и история', 'сохранятся полностью'],
                  ['Вернуть', toArchive ? 'тем же нажатием' : '—'],
                ],
                danger: toArchive,
                confirmLabel: toArchive ? 'Убрать в архив' : 'Вернуть',
              });
              if (!a) return;
              void fetch(`${API}/menu/locations/${loc}`, {
                method: 'PATCH', headers: jh,
                body: JSON.stringify({ isActive: !toArchive }),
              }).then(() => locations.refetch());
            })()}>Архив точки</button>
          <button className="btn small" title="Адрес витрины — касса напечатает QR «как вам?» на чеке"
            onClick={() => void (async () => {
              if (!loc) return;
              const host = window.location.host.replace(/^office\./, '');
              const a = await ask({
                title: 'QR-отзыв на чеке',
                sub: 'Касса напечатает на чеке QR: гость наводит камеру и попадает на витрину, где может оценить визит.',
                value: {
                  label: 'Адрес витрины',
                  initial: `https://menu.${host}`,
                  allowEmpty: true,
                  placeholder: 'https://menu.example.kz',
                  hint: 'пусто — не печатать QR на чеках',
                },
                confirmLabel: 'Сохранить',
              });
              if (!a) return;
              const cur = a.value;
              void fetch(`${API}/menu/locations/${loc}`, {
                method: 'PATCH', headers: jh,
                body: JSON.stringify({ publicUrl: cur || null }),
              }).then((r) => note(r.ok
                ? (cur ? 'Касса будет печатать QR-отзыв на чеках' : 'QR-отзыв на чеках выключен')
                : 'Не сохранилось — адрес должен начинаться с https://', r.ok ? 'ok' : 'bad'));
            })()}>QR-отзыв</button>
          <button className="btn small" title="Ссылка Kaspi «оставить на чай» — кнопка после оценки по QR"
            onClick={() => void (async () => {
              if (!loc) return;
              const a = await ask({
                title: 'Чаевые официанту',
                sub: 'После того как гость оценит визит по QR, ему покажут кнопку «оставить на чай».',
                value: {
                  label: 'Ссылка Kaspi для чаевых',
                  initial: 'https://pay.kaspi.kz/pay/',
                  allowEmpty: true,
                  hint: 'пусто — не показывать кнопку',
                },
                confirmLabel: 'Сохранить',
              });
              if (!a) return;
              const cur = a.value;
              void fetch(`${API}/menu/locations/${loc}`, {
                method: 'PATCH', headers: jh,
                body: JSON.stringify({ tipUrl: cur || null }),
              }).then((r) => note(r.ok
                ? (cur ? 'После оценки гость увидит «оставить на чай»' : 'Кнопка чаевых выключена')
                : 'Не сохранилось — адрес должен начинаться с https://', r.ok ? 'ok' : 'bad'));
            })()}>Чаевые</button>
          <button className="btn small" title="Лист QR всех столов: печать и на столы"
            onClick={() => void (async () => {
              if (!loc || !halls.data) return;
              const tables = halls.data.flatMap((hl) =>
                hl.tables.map((tb) => ({ ...tb, hall: hl.name })));
              if (tables.length === 0) { note('Столы ещё не расставлены', 'bad'); return; }
              const host = window.location.host.replace(/^office\./, '');
              const a = await ask({
                title: 'Печать QR по столам',
                sub: `Сделаем карточку на каждый стол — гость наводит камеру и открывает меню своего столика.`,
                effects: [['Столов', String(tables.length)], ['Что получится', 'страница на печать, по карточке на стол']],
                value: {
                  label: 'Адрес витрины',
                  initial: `https://menu.${host}`,
                  hint: 'к нему подставится номер стола',
                },
                confirmLabel: 'Собрать на печать',
              });
              if (!a) return;
              const base = a.value;
              const QR = await import('qrcode');
              const cards = await Promise.all(tables.map(async (tb) => {
                const url = `${base.replace(/\/$/, '')}/${loc}?t=${tb.id}`;
                const png = await QR.toDataURL(url, { width: 280, margin: 1 });
                return `<div class="card"><h2>${tb.hall} · ${tb.name}</h2>
                  <img src="${png}" alt="QR">
                  <p>Наведите камеру: меню · официант · счёт<br>
                  Камераны бағыттаңыз: мәзір · даяшы · шот</p></div>`;
              }));
              const w = window.open('', '_blank');
              if (!w) { note('Браузер закрыл окно печати — разрешите всплывающие окна для этого сайта', 'bad'); return; }
              w.document.write(`<!doctype html><html><head><meta charset="utf-8">
                <title>QR столов</title><style>
                body { font-family: system-ui, sans-serif; margin: 0; }
                .grid { display: grid; grid-template-columns: 1fr 1fr; }
                .card { border: 1px dashed #999; padding: 18px; text-align: center;
                  page-break-inside: avoid; }
                .card h2 { margin: 0 0 8px; font-size: 22px; }
                .card p { font-size: 12px; color: #444; margin: 8px 0 0; }
                img { width: 220px; height: 220px; }
                </style></head><body>
                <div class="grid">${cards.join('')}</div>
                <script>window.onload = () => window.print();</script>
                </body></html>`);
              w.document.close();
            })()}>Лист QR</button>
        </span>
        <button className="btn" title="Публичное меню для гостей — превратите в QR и на стол"
          onClick={() => {
            if (!loc) return;
            const host = window.location.host.replace(/^office\./, '');
            const url = `https://menu.${host}/${loc}`;
            void navigator.clipboard.writeText(url).then(
              () => note(`Ссылка на меню скопирована: ${url}`),
              () => void ask({
                title: 'Ссылка на меню',
                sub: 'Скопировать сама не смогла — выделите и скопируйте руками.',
                value: { label: 'Адрес витрины', initial: url, allowEmpty: true },
                confirmLabel: 'Закрыть',
              }),
            );
          }}>Ссылка на меню</button>
        <form className="row" onSubmit={(e) => {
          e.preventDefault();
          if (hallName.trim()) { createHall.mutate(hallName.trim()); setHallName(''); }
        }}>
          <input placeholder="Новый зал: «Основной», «Терраса»…"
            value={hallName} onChange={(e) => setHallName(e.target.value)} />
          <button className="btn" type="submit" disabled={!hallName.trim()}>Создать</button>
        </form>
        {locations.data && locations.data.length > 1 && (
          <label className="row">Точка
            <select value={loc} onChange={(e) => setLocationId(e.target.value)}>
              {locations.data!.map((l) => <option key={l.id} value={l.id}>{l.name}{l.isActive === false ? ' (архив)' : ''}</option>)}
            </select>
          </label>
        )}
      </div>

      {halls.data.length === 0 && (
        <p className="empty">Зал — это группа столов: «Основной», «Терраса», «VIP». Кассе нужны имена столов, чтобы кухня знала, куда нести.</p>
      )}

      {halls.data.map((hall) => (
        <section key={hall.id} className={`menu-cat ${hall.isActive ? '' : 'cat-archived'}`}>
          <header>
            <button className="name-btn" title="Переименовать"
              onClick={() => void (async () => {
                const a = await ask({
                  title: 'Название зала',
                  sub: 'Его видит официант на кассе, когда выбирает стол.',
                  value: { label: 'Как называется', initial: hall.name },
                  confirmLabel: 'Переименовать',
                });
                if (a) patchHall.mutate({ id: hall.id, name: a.value });
              })()}>
              <h2>{hall.name}{!hall.isActive && ' · в архиве'}</h2>
            </button>
            <span className="rule-tag">{hall.tables.filter((t) => t.isActive).length} столов</span>
            {/* Обслуживание всего зала: меняется один раз и действует
                на все столы, где не задано своего. Так владелец правит
                веранду или банкетный зал целиком */}
            <button className={`btn small ${hall.servicePct != null ? 'accent' : ''}`}
              title="Обслуживание для всего зала"
              onClick={() => {
                setSvcOwn(hall.servicePct != null ? String(hall.servicePct) : '');
                setServiceForHall(hall);
              }}>
              {hall.servicePct != null ? `Обсл. ${hall.servicePct}%` : 'Обсл. общее'}
            </button>
            <button className="btn small" onClick={() => setDraft({ hallId: hall.id, name: '', seats: '4' })}>
              + Стол
            </button>
            <button className="btn small" onClick={() => patchHall.mutate({ id: hall.id, isActive: !hall.isActive })}>
              {hall.isActive ? 'В архив' : 'Вернуть'}
            </button>
          </header>

          {draft?.hallId === hall.id && (
            <form className="menu-row draft" onSubmit={(e) => {
              e.preventDefault();
              const seats = Math.trunc(Number(draft.seats)) || 0;
              if (draft.name.trim() && seats >= 1) {
                addTable.mutate({ hallId: hall.id, name: draft.name.trim(), seats });
                setDraft(null);
              }
            }}>
              <input autoFocus placeholder="Имя: Т7, VIP-2…" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              <input placeholder="Мест" inputMode="numeric" value={draft.seats}
                onChange={(e) => setDraft({ ...draft, seats: e.target.value })} />
              <button className="btn small" type="submit">Сохранить</button>
              <button className="btn small" type="button" onClick={() => setDraft(null)}>Отмена</button>
            </form>
          )}

          <div className="tables-grid">
            {hall.tables.map((t) => (
              <div key={t.id} className={`table-card ${t.isActive ? '' : 'archived'}`}>
                <button className="name-btn" title="Переименовать"
                  onClick={() => void (async () => {
                    const a = await ask({
                      title: `Имя стола «${t.name}»`,
                      sub: 'Официант ищет стол по этому имени, а гость видит его в QR-меню.',
                      value: { label: 'Как называется', initial: t.name, hint: 'до 20 знаков' },
                      confirmLabel: 'Переименовать',
                    });
                    if (a) patchTable.mutate({ id: t.id, name: a.value.slice(0, 20) });
                  })()}>
                  <b>{t.name}</b>
                </button>
                <button className="seats" title="Изменить число мест"
                  onClick={() => void (async () => {
                    const a = await ask({
                      title: `Мест за «${t.name}»`,
                      sub: 'По этому числу подбирается стол под компанию при брони.',
                      effects: [['Сейчас', `${t.seats} мест`]],
                      value: { label: 'Сколько мест', initial: String(t.seats), numeric: true, hint: 'от 1 до 50' },
                      confirmLabel: 'Сохранить',
                    });
                    if (!a) return;
                    const seats = Math.trunc(Number(a.value));
                    if (seats >= 1 && seats <= 50) patchTable.mutate({ id: t.id, seats });
                    else note('Мест должно быть от 1 до 50', 'bad');
                  })()}>
                  {t.seats} мест
                </button>
                {/* Обслуживание у стола: банкетный зал берёт 10%, а барная
                    стойка ничего. Пусто — стол живёт по правилу зала
                    или заведения, и это самый частый случай */}
                <button className={`btn small ${t.servicePct != null ? 'accent' : ''}`}
                  title="Обслуживание для этого стола"
                  onClick={() => {
                    setSvcOwn(t.servicePct != null ? String(t.servicePct) : '');
                    setServiceFor(t);
                  }}>
                  {t.servicePct != null ? `Обсл. ${t.servicePct}%` : 'Обсл. общее'}
                </button>
                <button className="btn small" onClick={() => patchTable.mutate({ id: t.id, isActive: !t.isActive })}>
                  {t.isActive ? 'В архив' : 'Вернуть'}
                </button>
              </div>
            ))}
            {hall.tables.length === 0 && draft?.hallId !== hall.id && (
              <p className="cat-empty">Столов пока нет — добавьте первый.</p>
            )}
          </div>
        </section>
      ))}
      {(terminals.data?.rows.length ?? 0) > 0 && (
        <section className="menu-cat" style={{ marginTop: 16 }}>
          <header><h2>Кассы точки</h2></header>
          {terminals.data!.rows.map((t) => {
            const mins = t.lastSeenAt
              ? Math.floor((Date.now() - new Date(t.lastSeenAt).getTime()) / 60000)
              : null;
            const silent = mins === null || mins > 10;
            return (
              <div key={t.terminalId} className="menu-row term-row">
                <span className={`term-dot ${silent ? 'off' : 'on'}`} />
                <span className="name">{t.name}
                  {!t.activated && <small className="hint-inline"> · не активирована</small>}
                </span>
                <span className={`right ${silent && t.activated ? 'neg' : 'hint-inline'}`}>
                  {!t.activated ? '—'
                    : mins === null ? 'ещё не выходила на связь'
                    : mins === 0 ? 'на связи'
                    : mins < 60 ? `молчит ${mins} мин`
                    : `молчит ${Math.floor(mins / 60)} ч`}
                </span>
              </div>
            );
          })}
        </section>
      )}

      {loc && <HallPlanEditor token={token} locationId={loc} />}

    </div>

      {serviceFor && (
        <div className="dialog" onClick={() => setServiceFor(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <h2>Обслуживание · стол {serviceFor.name}</h2>
            <p className="hint">
              Процент добавляется к счёту после скидки и печатается в чеке
              отдельной строкой. У стола он важнее зального и общего.
            </p>

            <div className="svc-choices">
              {[null, 0, 5, 10, 15].map((v) => (
                <button key={String(v)}
                  className={`btn ${serviceFor.servicePct === v ? 'primary' : ''}`}
                  onClick={() => {
                    patchTable.mutate({ id: serviceFor.id, servicePct: v });
                    setServiceFor(null);
                  }}>
                  {v === null ? 'Как везде' : v === 0 ? 'Не берём' : `${v}%`}
                </button>
              ))}
            </div>

            {/* Частые значения кнопками, любое другое — полем.
                Двенадцать процентов бывает не реже десяти */}
            <label className="svc-own">
              Свой процент
              <input type="number" min={0} max={30} inputMode="numeric"
                placeholder="12"
                value={svcOwn}
                onChange={(e) => setSvcOwn(e.target.value)} />
              <button className="btn primary"
                disabled={svcOwn.trim() === '' || Number(svcOwn) < 0 || Number(svcOwn) > 30}
                onClick={() => {
                  patchTable.mutate({
                    id: serviceFor.id,
                    servicePct: Math.round(Number(svcOwn)),
                  });
                  setSvcOwn('');
                  setServiceFor(null);
                }}>
                Поставить
              </button>
            </label>

            <p className="hint">
              «Как везде» — стол берёт правило зала, а если там пусто,
              то общее правило заведения. Так работает большинство столов.
              «Не берём» — осознанный ноль: барная стойка, стол персонала.
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={() => setServiceFor(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {serviceForHall && (
        <div className="dialog" onClick={() => setServiceForHall(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <h2>Обслуживание · зал {serviceForHall.name}</h2>
            <p className="hint">
              Действует на все столы зала, где не задано своего процента.
              Столов с личным правилом: {serviceForHall.tables.filter((t) => t.servicePct != null).length}.
            </p>

            <div className="svc-choices">
              {[null, 0, 5, 10, 15].map((v) => (
                <button key={String(v)}
                  className={`btn ${serviceForHall.servicePct === v ? 'primary' : ''}`}
                  onClick={() => {
                    patchHall.mutate({ id: serviceForHall.id, servicePct: v });
                    setServiceForHall(null);
                  }}>
                  {v === null ? 'Как везде' : v === 0 ? 'Не берём' : `${v}%`}
                </button>
              ))}
            </div>

            <label className="svc-own">
              Свой процент
              <input type="number" min={0} max={30} inputMode="numeric" placeholder="12"
                value={svcOwn} onChange={(e) => setSvcOwn(e.target.value)} />
              <button className="btn primary"
                disabled={svcOwn.trim() === '' || Number(svcOwn) < 0 || Number(svcOwn) > 30}
                onClick={() => {
                  patchHall.mutate({
                    id: serviceForHall.id, servicePct: Math.round(Number(svcOwn)),
                  });
                  setSvcOwn('');
                  setServiceForHall(null);
                }}>
                Поставить
              </button>
            </label>

            {/* Столы с личным правилом зальный процент не подхватят —
                это их смысл. Но иногда владелец хочет ровно обратного:
                «сделай везде одинаково». Тогда сбрасываем разом */}
            {serviceForHall.tables.some((t) => t.servicePct != null) && (
              <button className="btn wide-warn"
                onClick={() => void (async () => {
                  const own = serviceForHall.tables.filter((t) => t.servicePct != null);
                  const a = await ask({
                    title: `Сбросить личные правила у ${own.length} столов`,
                    sub: 'Столы станут жить по правилу зала. Вернуть одним действием нельзя — проставлять придётся заново.',
                    effects: [
                      ['Зал', serviceForHall.name],
                      ['Столов со своим правилом', String(own.length)],
                      ['Станут', 'по правилу зала'],
                    ],
                    list: {
                      title: 'У кого сбросится',
                      rows: own.slice(0, 6).map((t) => `${t.name} · ${t.servicePct}%`),
                      more: own.length > 6 ? `…и ещё ${own.length - 6}` : undefined,
                    },
                    danger: true,
                    confirmLabel: 'Сбросить',
                  });
                  if (!a) return;
                  for (const t of own) patchTable.mutate({ id: t.id, servicePct: null });
                  setServiceForHall(null);
                  note(`Личные правила сброшены у ${own.length} столов`);
                })()}>
                Сбросить личные правила у {serviceForHall.tables.filter((t) => t.servicePct != null).length} стол(ов)
              </button>
            )}

            <div className="modal-actions">
              <button className="btn" onClick={() => setServiceForHall(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </>
  );
}